import { useEffect, useRef, useState } from 'react'
import { type Season, parseSeason, seasonFromDate } from '../../lib/season'
import type { TimeOfDay } from '../../lib/time-of-day'
import { supportsWebGL } from '../../lib/webgl'
import type { CafeScene, SceneState } from '../../scene/cafe-scene'

type Status = 'poster' | 'ready' | 'fallback'

/**
 * WebGL シーンを遅延読み込みする。読み込み前・WebGL 不可・初期化失敗では、
 * 同じシーンから撮っておいた季節・時間帯別のポスター（scripts/posters.ts）を出す。
 * celebrateKey が変わると完了演出を流す。
 */
export function CafeCanvas({
  state,
  celebrate,
  className = '',
  label,
  motionOff = false,
  bootAfter = 0,
}: {
  state: SceneState
  celebrate?: { key: number; seat: number | null }
  className?: string
  label: string
  /** 動きを止めて静止画にする（処理を軽くするモード） */
  motionOff?: boolean
  /** 開いてからこの時間（ms）は 3D を読み込まない。見出しの演出と重なって画面が引っかかるのを避ける */
  bootAfter?: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sceneRef = useRef<CafeScene | null>(null)
  const stateRef = useRef(state)
  stateRef.current = state
  const motionOffRef = useRef(motionOff)
  motionOffRef.current = motionOff
  const reducedQueryRef = useRef(false)
  const [status, setStatus] = useState<Status>('poster')
  // 手前の紅葉の色が季節で変わるので、ポスターも季節で選ぶ。?season=winter などで上書きできる
  const [season, setSeason] = useState<Season>(() => seasonFromDate(new Date()))
  useEffect(() => {
    const forced = parseSeason(new URLSearchParams(location.search).get('season'))
    if (forced) setSeason(forced)
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    // ?gl=0 で静止画に固定する（E2E テストと比較用）。
    // ?gl=1 はソフトウェア描画でも WebGL を動かす（SwiftShader で撮るスクリプト用）
    const gl = new URLSearchParams(location.search).get('gl')
    if (gl === '0') {
      setStatus('fallback')
      return
    }
    let disposed = false
    let cleanup = () => {}
    const boot = async () => {
      // WebGL が使えるかの確認も、試しにコンテキストを作るので重い（GPU の無い環境では最初の描画まで止まる）。読み込む直前に回す
      if (gl !== '1' && !supportsWebGL()) {
        setStatus('fallback')
        return
      }
      try {
        const { CafeScene } = await import('../../scene/cafe-scene')
        if (disposed) return
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
        const small = window.innerWidth < 768 || (navigator.hardwareConcurrency ?? 8) <= 4
        reducedQueryRef.current = reduced.matches
        const scene = new CafeScene(canvas, {
          reducedMotion: reduced.matches || motionOffRef.current,
          quality: small ? 'low' : 'high',
          season: parseSeason(new URLSearchParams(location.search).get('season')) ?? undefined,
        })
        // シェーダーを裏で組み立て終えてから描き始める（最初の1枚で画面が止まらないように）
        await scene.prepare()
        if (disposed) {
          scene.dispose()
          return
        }
        sceneRef.current = scene
        // 開発時だけ、QA 用にシーンを触れるようにする（完了演出の確認など）
        if (import.meta.env.DEV) (window as unknown as { __cafeScene?: CafeScene }).__cafeScene = scene
        const resize = () => {
          const r = canvas.getBoundingClientRect()
          scene.resize(r.width, r.height, window.devicePixelRatio || 1)
        }
        // 先に画面の縦横比を渡してから、最初のカメラ位置を決める
        resize()
        scene.setState(stateRef.current)
        const ro = new ResizeObserver(resize)
        ro.observe(canvas)
        let inView = true
        const io = new IntersectionObserver(([e]) => {
          inView = e.isIntersecting
          scene.setVisible(inView && !document.hidden)
        })
        io.observe(canvas)
        const onVis = () => scene.setVisible(inView && !document.hidden)
        document.addEventListener('visibilitychange', onVis)
        canvas.addEventListener('webglcontextlost', () => setStatus('fallback'))
        setStatus('ready')
        cleanup = () => {
          ro.disconnect()
          io.disconnect()
          document.removeEventListener('visibilitychange', onVis)
          scene.dispose()
          sceneRef.current = null
        }
      } catch (e) {
        console.warn('[cafe] WebGL を初期化できませんでした', e)
        if (!disposed) setStatus('fallback')
      }
    }
    // 最初の描画（静止画と文字）を優先する。three.js は重いので最初の操作（スクロール・タップ・ポインタ移動）で読み込み、
    // 操作が無くても一定時間後には読み込む
    let started = false
    const events = ['pointermove', 'pointerdown', 'touchstart', 'scroll', 'keydown', 'wheel'] as const
    const openedAt = performance.now()
    let wait = 0
    const begin = () => {
      if (started) return
      started = true
      for (const e of events) window.removeEventListener(e, begin)
      window.clearTimeout(timer)
      // 見出しの演出が終わるまで待ち、手の空いた時に読み込む
      wait = window.setTimeout(
        () => {
          if ('requestIdleCallback' in window)
            window.requestIdleCallback(() => void boot(), { timeout: 1000 })
          else void boot()
        },
        Math.max(0, bootAfter - (performance.now() - openedAt)),
      )
    }
    for (const e of events) window.addEventListener(e, begin, { passive: true, once: true })
    const timer = window.setTimeout(begin, Math.max(6000, bootAfter))
    return () => {
      disposed = true
      for (const e of events) window.removeEventListener(e, begin)
      window.clearTimeout(timer)
      window.clearTimeout(wait)
      cleanup()
    }
  }, [])

  useEffect(() => {
    sceneRef.current?.setState(state)
  }, [state])

  useEffect(() => {
    sceneRef.current?.setReducedMotion(reducedQueryRef.current || motionOff)
  }, [motionOff])

  useEffect(() => {
    if (celebrate && celebrate.key > 0) sceneRef.current?.celebrate(celebrate.seat)
  }, [celebrate?.key])

  const tod = state.timeOfDay
  return (
    <div className={`cafe-canvas cafe-canvas--${status} ${className}`} role="img" aria-label={label}>
      {/* キャンバスが灯るまでの下敷き。キャンバスはこの上にフェードインする */}
      <picture className="cafe-poster">
        <source media="(max-aspect-ratio: 1/1)" srcSet={`/posters/${season}-${tod}-portrait.webp`} />
        <img src={`/posters/${season}-${tod}-landscape.webp`} alt="" fetchPriority="high" decoding="async" />
      </picture>
      <canvas ref={canvasRef} className="cafe-canvas__gl" aria-hidden="true" />
    </div>
  )
}

export type { TimeOfDay }
